import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import {
  GroupType,
  PayableType,
  PERIOD,
  TransactionTarget,
  TransactionType,
} from 'src/app/core/enums/enums';
import {
  Content,
  Currency,
  ImageProfil,
  IRole,
  ITransaction,
} from 'src/app/core/models/models';
import { TextLanguagesPipe } from 'src/app/core/pipes/text-languages/text-languages.pipe';
import { UseCaseGroupService } from 'src/app/core/rest-services/rest-services';
import { ServicesService } from 'src/app/core/services/services.service';
import { toWordLocaleDateTime } from 'src/app/core/utils/datetime';
import { toMoney } from 'src/app/core/utils/misc';
import { OptionObject, PaginationElement } from 'src/app/core/utils/type';
import { CustomRangeCalendarComponent } from 'src/app/shared-components/custom-range-calendar/custom-range-calendar.component';

@Component({
  selector: 'app-white-label-transaction',
  templateUrl: './white-label-transaction.component.html',
  styleUrls: ['./white-label-transaction.component.scss'],
})
export class WhiteLabelTransactionComponent implements OnInit, OnDestroy {
  isLoading = false;
  page = 1;
  pagination: PaginationElement = { page: 1, totalPages: 1 };
  optionPeriodDropdown!: OptionObject[];
  loadingThrottle = 150;
  translations: any = {
    all: '',
    thisDay: '',
    thisWeek: '',
    thisMonth: '',
    thisYear: '',
    custom: '',
  };
  translationKeys: string[] = [
    'admin-fields.all-campaign',
    'admin-fields.day',
    'admin-fields.week',
    'admin-fields.month',
    'admin-fields.year',
    'admin-fields.custom-period',
  ];
  isTransitioning = false;
  currency!: Currency;
  language = '';
  transactions: ITransaction[] = [];
  currencies: Currency[] = [];
  avatar!: ImageProfil;
  transactionType = TransactionType;
  transactionTarget = TransactionTarget;
  $languageChange!: Subscription;
  $usedCredits!: Subscription;
  $userUpdate!: Subscription;
  filterPickedValue?: OptionObject;
  startDate!: Date;
  endDate!: Date;
  period: 'year' | 'month' | 'day' | 'all' | 'sevent_day' | 'custom' = 'all';
  startDateToDisplay = '';
  endDateToDisplay = '';
  textLanguagesPipe: TextLanguagesPipe;
  groupId!: number;
  groupType = GroupType;
  curentRole!: IRole;
  _PERIOD = PERIOD;
  isPDFLoading = false;
  previewtransactionId: number | undefined = undefined;
  payableType = PayableType;
  totalAmount = 0;

  constructor(
    private translateService: TranslateService,
    private usecaseGroupService: UseCaseGroupService,
    private services: ServicesService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.curentRole = this.services.sessionService.getCurrentRole();
    this.textLanguagesPipe = new TextLanguagesPipe(this.changeDetectorRef);
    this.currency = this.services.sessionService.getCurrencie();
    this.language = this.translateService.currentLang;

    // Initialization code can go here if needed
  }
  ngOnInit(): void {
    this.groupId = this.services.sessionService.getCurrentGroupId();
    this.listenForLanguageChangeEvents();
    this.getTranslations();
  }

  ngOnDestroy(): void {
    if (this.$languageChange) this.$languageChange.unsubscribe();
    if (this.$usedCredits) this.$usedCredits.unsubscribe();
    if (this.$userUpdate) this.$userUpdate.unsubscribe();
  }

  fetchTransaction(): void {
    this.isLoading = true;
    this.usecaseGroupService
      .list_transactions(
        this.groupId,
        this.period,
        this.period === PERIOD.CUSTOM
          ? moment(this.startDate).format('YYYY-MM-DD')
          : undefined,
        this.period === PERIOD.CUSTOM
          ? moment(this.endDate).format('YYYY-MM-DD')
          : undefined,
        this.page,
        this.transactionType.HEALTHCAREPACKAGEBUY
      )
      .subscribe({
        next: (res: Content<ITransaction>) => {
          this.transactions = res.data;
          this.transactions = this.transactions.filter(
            trans => trans.type === this.transactionType.HEALTHCAREPACKAGEBUY
          );
          this.calculateTotalAmount();
          this.pagination = {
            page: this.page,
            totalPages: res.last_page,
            total: res.total,
            perPage: res.per_page,
            form: res.from,
            to: res.to,
          };
        },
        error: () => {
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  calculateTotalAmount(): void {
    this.totalAmount = this.transactions
      .filter(
        transaction =>
          transaction.type === this.transactionType.HEALTHCAREPACKAGEBUY
      )
      .reduce((acc, transaction) => acc + (transaction.hc_amount || 0), 0);
    this.totalAmount = Math.abs(this.totalAmount); // Ensure total amount is positive
  }

  getFilter(): void {
    if (this.period === PERIOD.CUSTOM) {
      this.optionPeriodDropdown = [
        {
          label: this.translations.custom,
          value: PERIOD.CUSTOM,
        },
        {
          label: this.translations.thisWeek,
          value: PERIOD.WEEK,
        },
        {
          label: this.translations.thisMonth,
          value: PERIOD.MONTH,
        },
        {
          label: this.translations.thisYear,
          value: PERIOD.YEAR,
        },
        {
          label: this.translations.all,
          value: 'all',
        },
      ];
    } else {
      this.optionPeriodDropdown = [
        {
          label: this.translations.all,
          value: 'all',
        },
        {
          label: this.translations.thisWeek,
          value: PERIOD.WEEK,
        },
        {
          label: this.translations.thisMonth,
          value: PERIOD.MONTH,
        },
        {
          label: this.translations.thisYear,
          value: PERIOD.YEAR,
        },
        {
          label: this.translations.custom,
          value: PERIOD.CUSTOM,
        },
      ];
    }
  }

  handleCustomPeriod(): void {
    this.services.modalService
      .openModal(CustomRangeCalendarComponent, {
        width: '500px',
        height: 'auto',
        data: {
          incomingDateRange: new DateRange<Date>(this.startDate, this.endDate),
        },
      })
      .subscribe((range: DateRange<Date>) => {
        if (range) {
          this.period = PERIOD.CUSTOM;
          this.filterPickedValue = {
            label: this.translations.custom,
            value: PERIOD.CUSTOM,
          };
          this.getFilter();
          this.startDate = new Date(moment(range.start).format('MM/DD/YYYY'));
          this.endDate = new Date(moment(range.end).format('MM/DD/YYYY'));
          this.startDateToDisplay = moment(range.start).format('MM/DD/YYYY');
          this.endDateToDisplay = moment(range.end).format('MM/DD/YYYY');
          this.transactions = [];
          this.page = 1;
          this.fetchTransaction();
        }
      });
  }

  handlePeriodDropdownEvent(period: any): void {
    if (period !== 'open') {
      if (period.value === 'all') {
        this.period = 'all';
        this.transactions = [];
        this.page = 1;
        this.fetchTransaction();
        return;
      }
      this.period = period.value as PERIOD;

      if (this.period === PERIOD.CUSTOM) {
        this.services.modalService
          .openModal(CustomRangeCalendarComponent, {
            width: '500px',
            height: 'auto',
            data: {
              incomingDateRange: new DateRange<Date>(
                this.startDate,
                this.endDate
              ),
            },
          })
          .subscribe((range: DateRange<Date>) => {
            if (range) {
              this.startDate = new Date(
                moment(range.start).format('MM/DD/YYYY')
              );
              this.endDate = new Date(moment(range.end).format('MM/DD/YYYY'));
              this.startDateToDisplay = moment(range.start).format(
                'MM/DD/YYYY'
              );
              this.endDateToDisplay = moment(range.end).format('MM/DD/YYYY');
              this.transactions = [];
              this.page = 1;
              this.fetchTransaction();
            }
          });
      } else {
        this.getFilter();
        const rangePeriod: {
          startDate: Date;
          endDate: Date;
        } = this.services.utilsService.giveRangeOfPeriod(this.period);
        this.startDate = new Date(
          moment(rangePeriod.startDate).format('MM/DD/YYYY')
        );
        this.endDate = new Date(
          moment(rangePeriod.endDate).format('MM/DD/YYYY')
        );
        this.startDateToDisplay = moment(rangePeriod.startDate).format(
          'MM/DD/YYYY'
        );
        this.endDateToDisplay = moment(rangePeriod.endDate).format(
          'MM/DD/YYYY'
        );
        this.transactions = [];
        this.page = 1;
        this.fetchTransaction();
      }
    }
  }

  getTranslations(): void {
    this.translateService.get(this.translationKeys).subscribe(translations => {
      this.translations.all = translations['admin-fields.all-campaign'];
      this.translations.thisDay = translations['admin-fields.day'];
      this.translations.thisWeek = translations['admin-fields.week'];
      this.translations.thisMonth = translations['admin-fields.month'];
      this.translations.thisYear = translations['admin-fields.year'];
      this.translations.custom = translations['admin-fields.custom-period'];
      this.getFilter();
    });
  }

  setPage(el: number | symbol): void {
    if (typeof el !== 'symbol') {
      this.page = el;
      this.fetchTransaction();
    }
  }

  toLocaleDateTime(date: string): string {
    return toWordLocaleDateTime(new Date(date), this.language);
  }

  _toMoney(hc_amount: number) {
    return toMoney(hc_amount, this.currency.rate);
  }

  public listenForLanguageChangeEvents(): void {
    this.services.eventService.subscribe('change:language', () => {
      this.getTranslations();
    });
  }
}
